<script setup lang="ts">
import { type HTMLAttributes, computed, ref, onMounted } from "vue";
import {
	DialogClose,
	DialogContent,
	type DialogContentEmits,
	type DialogContentProps,
	DialogOverlay,
	DialogPortal,
	useForwardPropsEmits,
} from "radix-vue";
import { cn } from "@/lib/utils";
import { Cross2Icon } from "@radix-icons/vue";

interface ExtendedDialogContentProps extends DialogContentProps {
	"aria-describedby"?: string;
}

const props = defineProps<ExtendedDialogContentProps & { class?: HTMLAttributes["class"] }>();
const emits = defineEmits<DialogContentEmits>();

const isMobile = ref(false);

// Detecta se é mobile na inicialização
onMounted(() => {
	const updateMobileState = () => {
		isMobile.value = window.innerWidth < 768; // md breakpoint
	};

	updateMobileState();
	window.addEventListener("resize", updateMobileState);

	return () => {
		window.removeEventListener("resize", updateMobileState);
	};
});

const delegatedProps = computed(() => {
	const { class: _, "aria-describedby": __, ...delegated } = props;

	return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const ariaDescribedBy = computed(() => props["aria-describedby"]);

// Classes para desktop (modal centrado)
const desktopClasses = cn(
	"fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-90 data-[state=open]:zoom-in-90 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
);

// Classes para mobile (bottom sheet)
const mobileClasses = cn(
	"fixed bottom-0 left-0 right-0 z-50 grid w-full gap-4 border-t bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom rounded-t-lg max-h-[90vh] overflow-y-auto"
);

const contentClasses = computed(() =>
	isMobile.value ? mobileClasses : desktopClasses
);
</script>

<template>
    <DialogPortal>
        <DialogOverlay
            class="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogContent :aria-describedby="ariaDescribedBy" v-bind="forwarded" :class="cn(contentClasses, props.class)">
            <slot />

            <DialogClose
                class="absolute right-4 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-50 md:block hidden">
                <Cross2Icon class="w-4 h-4" />
                <span class="sr-only">Close</span>
            </DialogClose>

            <!-- Close button para mobile -->
            <div v-if="isMobile" class="absolute top-3 right-4">
                <DialogClose as-child>
                    <button class="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none" aria-label="Close dialog">
                        <Cross2Icon class="w-4 h-4" />
                        <span class="sr-only">Close</span>
                    </button>
                </DialogClose>
            </div>
        </DialogContent>
    </DialogPortal>
</template>
